import { Injectable } from '@angular/core';
// Nur der Typ - die Bibliothek selbst wird erst beim Drucken geholt (jspdf-laden.ts).
import type { jsPDF } from 'jspdf';
import { ladeJsPdf } from './jspdf-laden';
import { AufnahmePunktDocument, istPunktartMitProtokoll, bodenformString, isG, isM, idOf, klimastufeAnzeige, wasserformString, stfgString, roundToString, schichtLabel, BodenvegetationZeile, SubstratZeile, HorizontZeile, punktartBeschreibung } from '../../data-model';
import { schichtNWSKEval } from '../evaluators/nwsk';
import { karbonatEval } from '../evaluators/karbonat';
import { AUFLAGE_PLATZHALTER, AUFLAGE_ZEILEN, auflageSummeKurz, auflageTiefeBis } from './hl-area/humus-auflage';
import { FilzLage, angezeigteTiefe, filzHinweisPdf, filzLage, filzVersatz } from '../filz-tiefen';
import { flurTiefeText } from '../flur-tiefe';
import { baumartenRows, BaumartRow, de, dePos, FormFeld, FormSektion, grubeKopfSektionen, vegetationGruppen } from './grube-form-helpers';
import { installPdfUnicodeText } from './pdf-unicode';
import { WechselLage, wechselLagen, wechselLagenFuerAnzeige } from './wechsel-gruppen';
import { hatGrubenbild } from '../grubenbild/grubenbild-abschnitte';
import { grubenbildAlsBild, RasterBild } from '../grubenbild/grubenbild-als-bild';
import { PROFILBILD_FUSSNOTE } from '../grubenbild/grubenbild-erlaeuterung';
import { verfahrenVon } from '../verfahren/verfahren';
import { vegetationLayout } from './vegetation-layout';

/** Was der Aufrufer am Export waehlen kann. Ohne Angabe: das Protokoll wie bisher. */
export interface PdfOptionen {
	/**
	 * Hinter jedes Protokoll eine Seite mit dem Profilbild (dem Grubenbild aus
	 * dem gleichnamigen Reiter) und einer kurzen Erlaeuterung, wie es entsteht
	 * (Nutzer-Wunsch 25.09.2026).
	 */
	mitProfilbild?: boolean;
}

/** Spaltendefinition fuer die generische Tabellen-Ausgabe (renderColumnTable). */
interface PdfCol<T> {
	header: string;
	/** Relatives Spaltengewicht (wird auf die verfuegbare Breite skaliert). */
	w: number;
	align?: 'l' | 'r';
	get: (row: T, index: number) => string;
}

/**
 * Nachtrag je Zeile einer `renderColumnTable`: Zeilennummer, Oberkante und
 * Hoehe des Zeilenbandes sowie die Spaltenkanten und -breiten.
 */
type NachZeile = (idx: number, bandOben: number, hoehe: number, spaltenX: number[], spaltenBreite: number[]) => void;

/** Eine Zeile der Vegetationsliste in einer Spalte: welche Zeile, wie hoch (mm). */
interface SpaltenZeile {
	index: number;
	hoehe: number;
}

@Injectable({ providedIn: 'root' })
export class ProtokollPdfService {

	// Layout-Konstanten
	private readonly marginLeft = 25;   // Heftrand links
	private readonly marginRight = 10;
	private readonly marginTop = 12;
	private readonly marginBottom = 10;
	// Akzentfarbe für Sektionsüberschriften (dunkles Waldgrün)
	private readonly accentR = 60;
	private readonly accentG = 90;
	private readonly accentB = 60;
	// Helle Abtoenung der Akzentfarbe: hinterlegt die Standortkennwerte (HBF, FBF, HF, St.-STFG)
	private readonly tintR = 226;
	private readonly tintG = 235;
	private readonly tintB = 226;

	/**
	 * Generiert ein PDF für einen oder mehrere Bohrpunkte.
	 * @param optionen siehe `PdfOptionen`; ohne Angabe das Protokoll wie bisher.
	 */
	async generatePdf(bps: AufnahmePunktDocument[], optionen: PdfOptionen = {}): Promise<Blob> {
		console.log('[PDF] Starting PDF generation for', bps.length, 'points');

		const { jsPDF } = await ladeJsPdf();
		const doc = new jsPDF({
			orientation: 'portrait',
			unit: 'mm',
			format: 'a4'
		});
		// Pfeile/griechische Zeichen (z.B. Horizont-Symbol "Bb↔C") sonst als Zeichensalat.
		installPdfUnicodeText(doc);

		const pageWidth = doc.internal.pageSize.getWidth();
		const contentWidth = pageWidth - this.marginLeft - this.marginRight;

		// Seitenbereiche der Gruben merken, um danach die laufende Kopfzeile (Seite X von Y) zu stempeln.
		const grubeSpans: { bp: AufnahmePunktDocument; start: number; end: number }[] = [];

		// Schleife statt forEach: Das Profilbild wird im Browser gerastert, und
		// darauf muss gewartet werden.
		for (let index = 0; index < bps.length; index++) {
			const bp = bps[index];
			if (index > 0) {
				doc.addPage();
			}

			const startPage = doc.getNumberOfPages();
			let y = this.marginTop;
			const mitBild = !!optionen.mitProfilbild && this.bekommtProfilbild(bp);

			if (isG(bp)) {
				// Gruben (Punktart 2 + 4): eigener formular-getreuer SEA-95-Export
				this.renderGrubeForm(doc, bp, this.marginLeft, contentWidth);
				if (mitBild) await this.haengeProfilbildAn(doc, bp);
				grubeSpans.push({ bp, start: startPage, end: doc.getNumberOfPages() });
			} else if (istPunktartMitProtokoll(bp.type)) {
				y = this.renderAllgemeinSection(doc, bp, this.marginLeft, y, contentWidth);
				y = this.renderZusammenfassung(doc, bp, this.marginLeft, y, contentWidth);
				y = this.renderBodenSection(doc, bp, this.marginLeft, y, contentWidth);
				y = this.renderVegetation(doc, bp, this.marginLeft, y, contentWidth);
				this.renderFooter(doc, bp, this.marginLeft, contentWidth, y);
				if (mitBild) await this.haengeProfilbildAn(doc, bp);
			} else {
				y = this.renderKurzprotokoll(doc, bp, this.marginLeft, y, contentWidth);
			}
		}

		// Laufende Kopfzeile auf allen Grube-Seiten (Nr./Datum/Bearbeiter + „Seite X von Y").
		// Jede Seite kennt ihre Breite selbst: Vorderseite hoch, Profil quer, Profilbild wieder hoch.
		for (const span of grubeSpans) {
			const total = span.end - span.start + 1;
			for (let p = span.start; p <= span.end; p++) {
				doc.setPage(p);
				this.drawGrubeRunningHeader(doc, span.bp, p - span.start + 1, total, doc.internal.pageSize.getWidth());
			}
		}

		console.log('[PDF] PDF generation complete');
		return doc.output('blob');
	}

	/** Titel für den PDF-Kopf: Nummer, sonst gekürzte _id, sonst 'Ohne Nummer' */
	private punktTitel(bp: AufnahmePunktDocument): string {
		if (bp.header?.nummer) return String(bp.header.nummer);
		const id = (bp as any)._id;
		if (id) return `id:${String(id).substring(0, 8)}`;
		return 'Ohne Nummer';
	}

	/** Nummer-Zellinhalt in der Allgemein-Tabelle (leer = kurz-id, sonst leer) */
	private punktNummerCell(bp: AufnahmePunktDocument): string {
		if (bp.header?.nummer) return String(bp.header.nummer);
		const id = (bp as any)._id;
		if (id) return `id:${String(id).substring(0, 8)}`;
		return '';
	}

	/** Formatiert Datum von ISO zu dd.mm.yyyy */
	private formatDate(dateStr: string | undefined): string {
		if (!dateStr) return '';
		try {
			const date = new Date(dateStr);
			if (isNaN(date.getTime())) return dateStr;
			const day = date.getDate().toString().padStart(2, '0');
			const month = (date.getMonth() + 1).toString().padStart(2, '0');
			const year = date.getFullYear();
			return `${day}.${month}.${year}`;
		} catch {
			return dateStr;
		}
	}

	/**
	 * Zeichnet einen Sektions-Header mit farbigem Balken.
	 * @param tightTop Wenn true (Default): kompakter Anschluss an die vorige Sektion
	 *                 (kompensiert die Schriftbasislinien-Lücke der letzten Tabellenzeile).
	 *                 Beim ersten Header nach dem Titel false setzen.
	 */
	private renderSectionHeader(doc: jsPDF, label: string, margin: number, y: number, contentWidth: number, tightTop = true): number {
		if (tightTop) y -= 1;
		doc.setFillColor(this.accentR, this.accentG, this.accentB);
		doc.rect(margin, y, contentWidth, 5, 'F');
		doc.setFontSize(8);
		doc.setFont('helvetica', 'bold');
		doc.setTextColor(255, 255, 255);
		doc.text(label, margin + 2, y + 3.5);
		doc.setTextColor(0, 0, 0);
		return y + 9;
	}

	/** Rendert die Allgemein-Sektion als Tabelle */
	private renderAllgemeinSection(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const h = bp.header;
		const eh = bp.extendedHeader;
		let y = startY;

		// Titel
		doc.setFontSize(13);
		doc.setFont('helvetica', 'bold');
		doc.text('Aufnahmepunkt ' + this.punktTitel(bp), margin, y);
		// Feine Linie unter dem Titel
		y += 1.5;
		doc.setDrawColor(this.accentR, this.accentG, this.accentB);
		doc.setLineWidth(0.5);
		doc.line(margin, y, margin + contentWidth, y);
		doc.setLineWidth(0.2);
		y += 5;

		doc.setFontSize(8);
		const lineHeight = 4;
		const col1 = margin;
		const col2 = margin + 24;
		const col3 = margin + contentWidth / 2 + 5;
		const col4 = col3 + 24;

		// --- Block: Aufnahme (erster Header, kein Tight-Top weil Abstand zum Titel gewünscht) ---
		y = this.renderSectionHeader(doc, 'Aufnahme', margin, y, contentWidth, false);

		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Punktart', this.shortenPunktart(bp.type), 'Datum', this.formatDate(h.aufnahme?.datum));
		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Nummer', this.punktNummerCell(bp), 'Kartierer', h.aufnahme?.kartierer || '');
		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Labornummer', h.labornummer ? String(h.labornummer) : '', 'Laborstatus', h.statusLaboranalyse || '');

		// --- Block: Adresse ---
		y = this.renderSectionHeader(doc, 'Adresse', margin, y, contentWidth);

		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Forstamt', h.forstamt || '', 'Revier', h.revier || '');

		// Forstadresse: Abt+Ua+Tf zusammengezogen (z.B. "4536a1")
		const forstadresse = [h.adresse?.abteilung, h.adresse?.unterabteilung, h.adresse?.teilflaeche].filter(x => x).join('');
		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Forstbetrieb', h.forstbetrieb || '', 'Forstadresse', forstadresse);

		// Gem/Flur/FS links, Koordinaten + Projektion rechts (Projektion in Klammern)
		const gemParts: string[] = [];
		if (h.adresse?.gemarkung) gemParts.push(h.adresse.gemarkung);
		if (h.adresse?.flur) gemParts.push('Flur ' + h.adresse.flur);
		if (h.adresse?.flurstueck) gemParts.push('FS ' + h.adresse.flurstueck);
		const gemFlurFs = gemParts.join(' / ');
		const koord = h.koordinaten;
		let koordStr = '';
		if (koord && (koord.x || koord.y)) {
			koordStr = `${this.formatKoordinate(koord.x, koord.system)} / ${this.formatKoordinate(koord.y, koord.system)}`;
			if (koord.system) koordStr += ` (${koord.system})`;
		}
		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Gem/Flur/FS', gemFlurFs, 'Koordinaten', koordStr);

		// --- Block: Naturraum ---
		y = this.renderSectionHeader(doc, 'Naturraum', margin, y, contentWidth);

		// Wuchsgebiet/Wuchsbezirk: Werte sind oft sehr lang → mit '…' kürzen, statt rechts zu überschreiben.
		const wgMax = col3 - col2 - 2;
		const wbMax = margin + contentWidth - col4 - 2;
		const wgText = this.truncateToWidth(doc, eh?.wuchsgebiet || '', wgMax);
		const wbText = this.truncateToWidth(doc, eh?.wuchsbezirk || '', wbMax);
		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Wuchsgebiet', wgText, 'Wuchsbezirk', wbText);

		const geologie = [eh?.geologie?.oben, eh?.geologie?.trenner, eh?.geologie?.unten].filter(g => g).join(' ');
		// Handeintrag vor automatisch geholtem Wert (siehe klimastufeAnzeige)
		const klimastufe = klimastufeAnzeige(eh?.klimaStufe);

		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Geologie', geologie, 'Klimastufe', klimastufe);

		// Moortyp nur bei M-Punkten
		if (isM(bp) && eh?.moorTyp) {
			y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
				'Moortyp (ökol.)', eh.moorTyp.oekologisch || '', 'Moortyp (hydr.)', eh.moorTyp.hydrogenetisch || '');
		}

		y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
			'Relief', this.buildReliefString(bp), 'Mesoklima', eh?.mesoklima || '');

		// Morphologie und KMgCaP nur bei Nicht-Moor
		if (!isM(bp)) {
			const morphologie = eh?.morphologie?.filter(m => m).join('/') || '';
			y = this.drawTableRow(doc, y, lineHeight, col1, col2, col3, col4, contentWidth, margin,
				'Morphologie', morphologie, 'KMgCaP', eh?.kMgCaPSerie || '');
		}

		// --- Block: Bestand / Bemerkungen / Witterung ---
		y = this.renderSectionHeader(doc, 'Bestand / Bemerkungen / Witterung', margin, y, contentWidth);

		const baumartenStr = this.formatBaumarten(eh?.baumarten);
		if (baumartenStr) {
			doc.setFont('helvetica', 'bold');
			doc.text('Bestand:', col1, y);
			doc.setFont('helvetica', 'normal');
			const baumartenLines = doc.splitTextToSize(baumartenStr, contentWidth - 25);
			doc.text(baumartenLines, col2, y);
			y += Math.max(baumartenLines.length * lineHeight, lineHeight);
		}

		if (h.bemerkungen) {
			doc.setFont('helvetica', 'bold');
			doc.text('Bemerkungen:', col1, y);
			doc.setFont('helvetica', 'normal');
			const bemLines = doc.splitTextToSize(h.bemerkungen, contentWidth - 25);
			doc.text(bemLines, col2, y);
			y += Math.max(bemLines.length * lineHeight, lineHeight);
		}

		if (eh?.witterung) {
			doc.setFont('helvetica', 'bold');
			doc.text('Witterung:', col1, y);
			doc.setFont('helvetica', 'normal');
			const witterungLines = doc.splitTextToSize(String(eh.witterung), contentWidth - 25);
			doc.text(witterungLines, col2, y);
			y += Math.max(witterungLines.length * lineHeight, lineHeight);
		}

		return y;
	}

	/** Formatiert Baumarten für die Anzeige */
	private formatBaumarten(baumarten: any): string {
		if (!baumarten) return '';
		if (typeof baumarten === 'string') return baumarten;
		if (Array.isArray(baumarten)) {
			return baumarten.map(b => `${b.art || ''} ${b.alter || ''}j`).filter(s => s.trim()).join(', ');
		}
		return '';
	}

	/** Formatiert einen Koordinatenwert je nach Projektion (Lat/Lon mit Dezimalen, sonst ganzzahlig) */
	private formatKoordinate(v: number | undefined, system: string | undefined): string {
		if (v === undefined || v === null || isNaN(v)) return '';
		// Geografische Systeme: 6 Nachkommastellen
		if (system === 'EPSG:4326' || system === 'EPSG:4258') {
			return v.toFixed(6);
		}
		// Projizierte Systeme: ganzzahlig
		return Math.round(v).toString();
	}

	/** Kürzt einen String auf maxWidth und hängt '…' an, wenn er zu lang ist. */
	private truncateToWidth(doc: jsPDF, text: string, maxWidth: number): string {
		if (!text) return '';
		if (doc.getTextWidth(text) <= maxWidth) return text;
		const ellipsis = '…';
		let result = text;
		while (result.length > 0 && doc.getTextWidth(result + ellipsis) > maxWidth) {
			result = result.slice(0, -1);
		}
		return result + ellipsis;
	}

	/** Zeichnet eine Tabellenzeile mit 4 Spalten. Zu lange Werte werden umgebrochen statt rechts in die nächste Spalte zu laufen; die Zeilenhöhe wächst entsprechend mit. */
	private drawTableRow(doc: jsPDF, y: number, lineHeight: number, col1: number, col2: number, col3: number, col4: number, contentWidth: number, margin: number,
		label1: string, value1: string, label2: string, value2: string): number {
		// Verfügbare Breite je Wertespalte (mit kleinem Abstand vor der nächsten Spalte/dem Rand)
		const value1Max = col3 - col2 - 2;
		const value2Max = margin + contentWidth - col4 - 2;

		doc.setFont('helvetica', 'bold');
		doc.text(label1 + ':', col1, y);
		doc.setFont('helvetica', 'normal');
		const lines1: string[] = value1 ? doc.splitTextToSize(value1, value1Max) : [];
		lines1.forEach((line, i) => doc.text(line, col2, y + i * lineHeight));

		let lines2: string[] = [];
		if (label2) {
			doc.setFont('helvetica', 'bold');
			doc.text(label2 + ':', col3, y);
			doc.setFont('helvetica', 'normal');
			lines2 = value2 ? doc.splitTextToSize(value2, value2Max) : [];
			lines2.forEach((line, i) => doc.text(line, col4, y + i * lineHeight));
		}

		const rowLines = Math.max(lines1.length, lines2.length, 1);
		return y + rowLines * lineHeight;
	}

	/** Beschreibung der Punktart (Single Source aus der Punktarten-Liste). */
	private shortenPunktart(type: string | undefined): string {
		if (!type) return '';
		return punktartBeschreibung(type) || type;
	}

	/**
	 * Humusauflage (Ol/Of/Oh, Mächtigkeit in cm) als eine Zeile nebeneinander:
	 * unter der Überschrift „Horizontfolge", über deren Tabelle.
	 */
	private renderHumusAuflage(doc: jsPDF, bp: AufnahmePunktDocument, startX: number, endX: number, y: number): void {
		const auflage = bp.extendedHeader?.humusAuflage;
		const felder: [string, string][] = [
			['Ol', de(auflage?.ol)],
			['Of', de(auflage?.of)],
			['Oh', de(auflage?.oh)],
		];

		const schrift = doc.getFont();
		const groesse = doc.getFontSize();
		const spaltenBreite = (endX - startX) / felder.length;

		doc.setFontSize(6.5);
		felder.forEach(([label, wert], i) => {
			const x = startX + i * spaltenBreite + 1;
			doc.setFont('helvetica', 'bold');
			doc.text(label, x, y);
			doc.setFont('helvetica', 'normal');
			doc.text(wert, x + 5, y);
		});

		// Zeichenzustand wiederherstellen (die Tabellen-Header darunter erwarten ihn unveraendert).
		doc.setFont(schrift.fontName, schrift.fontStyle);
		doc.setFontSize(groesse);
	}

	/** Rendert die Boden-Sektion (Substrat- und Horizontfolge) */
	private renderBodenSection(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const substratfolge = bp.Substratfolge || [];
		const horizontfolge = bp.Horizontfolge || [];
		let y = startY;

		const isMoor = isM(bp);
		const rowHeight = 3.5;
		const tableEndX = margin + contentWidth;
		// Filz: Tiefen ab Oberkante Ol (filz-tiefen.ts) - die Auflage steht dann
		// als Zeilen in der Tabelle, und alles darunter rueckt um sie nach unten.
		const lage = filzLage(bp);

		// Spaltenbreiten gleichmäßig verteilen
		// Substrat: ca. 65% der Breite, Horizont: ca. 35%
		const substratWidth = contentWidth * 0.65;
		const horizontWidth = contentWidth * 0.35 - 2; // -2 für Trennlinie

		// B/G-Punkte (!isM): Tiefe, Mächt, W, Substrat, Humus, LagD, Skel, Karb, nWSK (9 Spalten, KEINE Farbe)
		// M-Punkte (isM): Tiefe, Mächt, W, T1, T2, T3, Substrat, Zers, Kons, Skel, Karb, SubV, Bes, Farbe, nWSK (15 Spalten)
		let headers: string[];
		let colCount: number;
		let sColWidths: number[]; // Individuelle Spaltenbreiten

		// Feste schmale Breiten für Tiefe, Mächtigkeit und W (mit Platz nach Mächtigkeit)
		const tiefeWidth = 7;
		const maechtWidth = 10; // Abstand nach rechts vor W
		const wWidth = 8; // Breiter für '<->' Symbol
		const fixedWidth = tiefeWidth + maechtWidth + wWidth;

		if (isMoor) {
			colCount = 15;
			headers = ['Tiefe', 'Mächt', 'W', 'T1', 'T2', 'T3', 'Substrat', 'Zers', 'Kons', 'Skel', 'Karb', 'SubV', 'Bes.', 'Farbe', 'nWSK'];
			const remainingWidth = substratWidth - fixedWidth;
			const otherColWidth = remainingWidth / 12; // 12 weitere Spalten
			sColWidths = [tiefeWidth, maechtWidth, wWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth];
		} else {
			colCount = 9;
			headers = ['Tiefe', 'Mächt', 'W', 'Substrat', 'Humus', 'LagD', 'Skel', 'Karb', 'nWSK'];
			const remainingWidth = substratWidth - fixedWidth;
			const otherColWidth = remainingWidth / 6; // 6 weitere Spalten
			sColWidths = [tiefeWidth, maechtWidth, wWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth, otherColWidth];
		}

		// Spaltenpositionen aus individuellen Breiten berechnen
		const sColPos: number[] = [];
		let currentX = margin;
		for (let i = 0; i < colCount; i++) {
			sColPos.push(currentX);
			currentX += sColWidths[i];
		}
		const substratEndX = margin + substratWidth;

		// Horizont: 5 Spalten - Tiefe, Mächt, H1, H2, H3 (schmale Tiefe/Mächt, Rest gleichmäßig)
		const hStartX = substratEndX + 2;
		const hColCount = 5;
		const hTiefeWidth = 7;
		const hMaechtWidth = 12; // Breiter für mehr Abstand nach rechts vor H1
		const hFixedWidth = hTiefeWidth + hMaechtWidth;
		const hOtherColWidth = (horizontWidth - hFixedWidth) / 3; // 3 Horizont-Spalten
		const hColWidths = [hTiefeWidth, hMaechtWidth, hOtherColWidth, hOtherColWidth, hOtherColWidth];
		const hColPos: number[] = [];
		let hCurrentX = hStartX;
		for (let i = 0; i < hColCount; i++) {
			hColPos.push(hCurrentX);
			hCurrentX += hColWidths[i];
		}

		const hHeaders = ['Tiefe', 'Mächt', 'H1', 'H2', 'H3'];

		// Überschriften für die Tabellenbereiche (im Akzentbalken).
		// Schriftbasislinien-Lücke der vorigen Sektion kompensieren (analog renderSectionHeader).
		y -= 1;
		doc.setFillColor(this.accentR, this.accentG, this.accentB);
		doc.rect(margin, y, substratEndX - margin, 5, 'F');
		doc.rect(hStartX, y, tableEndX - hStartX, 5, 'F');
		doc.setFontSize(8);
		doc.setFont('helvetica', 'bold');
		doc.setTextColor(255, 255, 255);
		doc.text('Substratfolge', margin + (substratEndX - margin) / 2, y + 3.5, { align: 'center' });
		doc.text('Horizontfolge', hStartX + (tableEndX - hStartX) / 2, y + 3.5, { align: 'center' });
		doc.setTextColor(0, 0, 0);

		// Humusauflage (Ol/Of/Oh) nebeneinander unter der Überschrift, über der Horizont-Tabelle.
		// Beide Tabellen starten danach wieder auf gleicher Höhe (Zeilen bleiben ausgerichtet).
		// Bei einem Filz NICHT hier: Dort steht die Auflage als Zeilen in der Tabelle
		// (renderFilzAuflage), denn sie gehoert zum Profil.
		y += 8;
		if (!lage.filz) {
			this.renderHumusAuflage(doc, bp, hStartX, tableEndX, y);
			y += 5.5;
		}

		// Header-Zeile mit grauem Hintergrund
		doc.setFillColor(210, 210, 210);
		doc.rect(margin, y - 2.5, substratEndX - margin, 3.5, 'F');
		doc.rect(hStartX, y - 2.5, tableEndX - hStartX, 3.5, 'F');

		// Spaltenheader
		doc.setFontSize(6.5);
		headers.forEach((hdr, i) => {
			if (i < sColPos.length) {
				// nWSK (letzte Spalte) rechtsbündig
				if (i === colCount - 1) {
					doc.text(hdr, sColPos[i] + sColWidths[i] - 2, y, { align: 'right' });
				} else {
					doc.text(hdr, sColPos[i] + 1, y);
				}
			}
		});
		hHeaders.forEach((hdr, i) => doc.text(hdr, hColPos[i] + 1, y));

		y += 1;
		// Linie unter Header
		doc.setDrawColor(100);
		doc.line(margin, y, tableEndX, y);
		// Vertikale Trennlinie im Header (ab Spaltenheader-Oberkante)
		doc.line(substratEndX + 1, y - 4, substratEndX + 1, y);

		const headerEndY = y;
		y += rowHeight;

		doc.setFont('helvetica', 'normal');
		doc.setFontSize(7);

		// Filz: Die Auflage gehoert zum Profil und steht als drei Zeilen IN der
		// Tabelle, mit „Tiefe bis" ab Oberkante Ol; die Gelaendeoberflaeche ist
		// die dicke Linie darueber. Alle Tiefen darunter ruecken um die Auflage
		// nach unten (filz-tiefen.ts, Nutzer-Entscheidung 25.09.2026).
		if (lage.filz) {
			y = this.renderFilzAuflage(doc, bp, margin, substratEndX, hStartX, tableEndX, hColPos, hColWidths, headerEndY, y, rowHeight);
		}

		// Merged-Rows: Substrat und Horizont haben unabhängige Zeilenzahlen
		const mergedRows = this.buildMergedRows(substratfolge, horizontfolge);
		// Wechsel-Klammer: Der Haken heisst „wechselt mit der Zeile darunter"
		// (Regel in wechsel-gruppen.ts, dieselbe wie in der App). Zeilen ohne
		// Substrat ziehen die Linie durch, sonst risse sie mitten in einer
		// Kette auf.
		const lagen = wechselLagenFuerAnzeige(substratfolge, mergedRows.map(r => r.substratIndex));

		mergedRows.forEach((row, idx) => {
			const s = row.substrat;
			const hz = row.horizont;

			// Striped background - getrennt für Substrat und Horizont basierend auf deren Index
			if (row.substratStripe) {
				doc.setFillColor(242, 242, 242);
				doc.rect(margin, y - 2.5, substratEndX - margin, rowHeight, 'F');
			}
			if (row.horizontStripe) {
				doc.setFillColor(242, 242, 242);
				doc.rect(hStartX, y - 2.5, tableEndX - hStartX, rowHeight, 'F');
			}

			// Klammer der Wechselschichtung — auch in Zeilen ohne Substrat, damit
			// die Linie durchlaeuft.
			this.zeichneWechselKlammer(doc, lagen[idx], sColPos[2], y - 2.5, rowHeight);

			// Substrat-Daten (nur wenn Substrat-Eintrag vorhanden)
			if (s && row.substratIndex !== null) {
				// Tiefe und Mächtigkeit rechtsbündig mit Abstand vom Rand (Mächtigkeit mit mehr Abstand zu W)
				doc.text(this.tiefeText(s.tiefe, lage), sColPos[0] + sColWidths[0] - 1, y, { align: 'right' });
				doc.text(s.maechtigkeit?.toString() || '', sColPos[1] + sColWidths[1] - 5, y, { align: 'right' });
				doc.text(s.wechselschichtung ? 'W' : '', sColPos[2] + 1, y);

				// nWSK über Evaluator berechnen
				const nWskValue = this.schichtNWSK(bp, row.substratIndex);
				// nWSK rechtsbündig am rechten Rand der Zelle (letzte Spalte), mit Abstand vom Rand
				const nWskColRight = sColPos[colCount - 1] + sColWidths[colCount - 1] - 2;

				if (isMoor) {
					// M-Punkte: T1, T2, T3, Substrat, Zers, Kons, Skel, Karb, SubV, Bes, Farbe, nWSK
					doc.text(s.torfarten?.[0] || '', sColPos[3] + 1, y);
					doc.text(s.torfarten?.[1] || '', sColPos[4] + 1, y);
					doc.text(s.torfarten?.[2] || '', sColPos[5] + 1, y);
					doc.text(s.substrat || '', sColPos[6] + 1, y);
					doc.text(s.torfZersetzungsgrad?.toString() || '', sColPos[7] + 1, y);
					doc.text(s.muddeKonsistenz?.toString() || '', sColPos[8] + 1, y);
					doc.text(s.skelettgehalt?.toString() || '', sColPos[9] + 1, y);
					doc.text(s.karbonatgehalt?.toString() || '', sColPos[10] + 1, y);
					doc.text(s.substanzvolumen?.toString() || '', sColPos[11] + 1, y);
					const bes = (s.besonderheiten || '').substring(0, 6);
					doc.text(bes, sColPos[12] + 1, y);
					doc.text(s.farbe || '', sColPos[13] + 1, y);
					// nWSK rechtsbündig
					doc.text(nWskValue, nWskColRight, y, { align: 'right' });
				} else {
					// B/G-Punkte: Substrat, Humus, LagD, Skel, Karb, nWSK (ohne Farbe)
					doc.text(s.substrat || '', sColPos[3] + 1, y);
					doc.text(s.humusgehalt?.toString() || '', sColPos[4] + 1, y);
					doc.text(s.lagerungsdichte?.toString() || '', sColPos[5] + 1, y);
					doc.text(s.skelettgehalt?.toString() || '', sColPos[6] + 1, y);
					doc.text(s.karbonatgehalt?.toString() || '', sColPos[7] + 1, y);
					// nWSK rechtsbündig
					doc.text(nWskValue, nWskColRight, y, { align: 'right' });
				}
			}

			// Horizont-Daten (nur wenn Horizont-Eintrag vorhanden)
			if (hz) {
				// Tiefe und Mächtigkeit rechtsbündig mit Abstand vom Rand (Mächtigkeit mit mehr Abstand zu H1)
				doc.text(this.tiefeText(hz.tiefe, lage), hColPos[0] + hColWidths[0] - 1, y, { align: 'right' });
				doc.text(hz.maechtigkeit?.toString() || '', hColPos[1] + hColWidths[1] - 5, y, { align: 'right' });
				const horizonte = hz.horizonte || [];
				// 3 Horizont-Spalten separat
				doc.text(horizonte[0] || '', hColPos[2] + 1, y);
				doc.text(horizonte[1] || '', hColPos[3] + 1, y);
				doc.text(horizonte[2] || '', hColPos[4] + 1, y);
			}

			y += rowHeight;
		});

		// Unterlinie
		doc.setDrawColor(150);
		doc.line(margin, y, tableEndX, y);
		// Vertikale Trennlinie durchgehend
		doc.line(substratEndX + 1, headerEndY, substratEndX + 1, y);

		// Filz: sagen, wie die Tiefen zu lesen sind - sonst haelt jemand die
		// verschobene Zahl fuer die eingegebene.
		if (lage.filz) {
			y += 3;
			doc.setFontSize(6.5);
			doc.setTextColor(90);
			doc.text(filzHinweisPdf(lage.versatz), margin + 1, y);
			doc.setTextColor(0, 0, 0);
			doc.setFontSize(7);
		}

		return y;
	}

	/** Eine Tiefe, wie sie im Protokoll steht: eingegeben ab Mineralboden, bei Filz um die Auflage tiefer. */
	private tiefeText(tiefe: number | undefined, lage: FilzLage): string {
		return angezeigteTiefe(tiefe, lage.versatz)?.toString() ?? '';
	}

	/**
	 * Filz: die Auflage als drei Zeilen oben in der Horizont-Tabelle (Ol, Of,
	 * Oh mit „Tiefe bis" ab Oberkante Ol und Maechtigkeit), die dicke Linie
	 * darueber ist die Gelaendeoberflaeche - wie in der Boden-Uebersicht. Auf
	 * der Substratseite steht nur „organische Auflage · 12 cm": Die Auflage
	 * hat keine Bodenart. Liefert die y-Position der ersten Mineralzeile.
	 */
	private renderFilzAuflage(
		doc: jsPDF, bp: AufnahmePunktDocument, margin: number, substratEndX: number, hStartX: number,
		tableEndX: number, hColPos: number[], hColWidths: number[], headerEndY: number, y: number, rowHeight: number,
	): number {
		const auflage = bp.extendedHeader?.humusAuflage;

		// Gelaendeoberflaeche: dick, ueber der ganzen Breite, an der Oberkante des Blocks.
		doc.setDrawColor(108, 117, 125);
		doc.setLineWidth(0.6);
		doc.line(margin, headerEndY, tableEndX, headerEndY);
		doc.setLineWidth(0.2);
		doc.setDrawColor(100);

		// Der Block leicht grau hinterlegt, wie am Schirm.
		doc.setFillColor(247, 247, 247);
		doc.rect(margin, y - 2.5, substratEndX - margin, rowHeight * AUFLAGE_ZEILEN.length, 'F');
		doc.rect(hStartX, y - 2.5, tableEndX - hStartX, rowHeight * AUFLAGE_ZEILEN.length, 'F');

		AUFLAGE_ZEILEN.forEach((zeile, i) => {
			if (i === 0) {
				const summe = auflageSummeKurz(auflage);
				doc.setTextColor(108, 117, 125);
				doc.text(summe ? `${AUFLAGE_PLATZHALTER} · ${summe}` : AUFLAGE_PLATZHALTER, margin + 1, y);
				doc.setTextColor(0, 0, 0);
			}
			const tiefe = auflageTiefeBis(auflage, zeile.schluessel);
			const wert = auflage?.[zeile.schluessel] as number | undefined;
			doc.text(tiefe?.toString() ?? '', hColPos[0] + hColWidths[0] - 1, y, { align: 'right' });
			doc.text(wert?.toString() ?? '', hColPos[1] + hColWidths[1] - 5, y, { align: 'right' });
			doc.text(zeile.feld, hColPos[2] + 1, y);
			y += rowHeight;
		});
		return y;
	}

	/**
	 * Zeichnet die Klammer neben der Spalte „W" ueber die Zeilen, die im
	 * Wechsel miteinander liegen. Der Haken sagt „wechselt mit der Zeile
	 * darunter"; die Klammer macht sichtbar, welche Zeilen damit
	 * zusammengehoeren — auf Papier ist Nachzaehlen noch muehsamer als am
	 * Schirm, weil nichts anklickbar ist.
	 *
	 * `x` ist der linke Rand der W-Spalte, `breite` ihre Breite und `bandOben`
	 * die Oberkante des Zeilenbandes.
	 *
	 * Sie oeffnet sich nach RECHTS, zu den Substraten hin — wie am Schirm
	 * (Nutzer-Entscheidung 19.09.2026, siehe bohrpunkt-boden.component.css).
	 * Die Arme zeigen auf das, was im Wechsel liegt, nicht auf den Buchstaben.
	 * Bis zum 20.09.2026 lag sie auf dem Papier andersherum.
	 */
	private zeichneWechselKlammer(doc: jsPDF, lage: WechselLage, x: number, bandOben: number, hoehe: number, breite = 8): void {
		if (!lage || lage === 'keine') return;

		// Die Arme enden dicht am rechten Spaltenrand, die Senkrechte steht um
		// ihre Laenge weiter links — so bleibt die Klammer im selben Streifen
		// wie zuvor und laeuft dem Buchstaben nicht ins Gehege.
		const armEndeX = x + breite - 0.6;
		const linieX = armEndeX - 1.2;
		const mitte = bandOben + hoehe / 2;
		const unten = bandOben + hoehe;

		// Zeichenzustand merken: Die uebrigen Linien des Protokolls erwarten ihn
		// unveraendert.
		const alteLinienbreite = typeof doc.getLineWidth === 'function' ? doc.getLineWidth() : 0.2;
		doc.setDrawColor(0);
		doc.setLineWidth(0.3);

		// Senkrechte: ganz durch (mitte), ab der Zeilenmitte nach unten (anfang)
		// oder von oben bis zur Zeilenmitte (ende).
		const von = lage === 'anfang' ? mitte : bandOben;
		const bis = lage === 'ende' ? mitte : unten;
		doc.line(linieX, von, linieX, bis);

		// Arm nach rechts, zu den Substraten hin — nur am Anfang und am Ende
		// der Kette.
		if (lage === 'anfang' || lage === 'ende') doc.line(linieX, mitte, armEndeX, mitte);

		doc.setLineWidth(alteLinienbreite);
	}

	/** Baut Merged-Rows für Substrat/Horizont-Tabelle (analog zur Angular-Komponente) */
	private buildMergedRows(substratfolge: any[], horizontfolge: any[]): {substrat: any, horizont: any, substratIndex: number | null, substratStripe: boolean, horizontStripe: boolean}[] {
		// Tiefen mit Indizes sammeln
		const substratTiefen = substratfolge.map((s, i) => ({ tiefe: s.tiefe, index: i }));
		const horizontTiefen = horizontfolge.map((h, i) => ({ tiefe: h.tiefe, index: i }));

		// Alle einzigartigen Tiefen sammeln und sortieren
		const alleTiefen = new Set<number>();
		substratTiefen.forEach(s => { if (s.tiefe !== undefined) alleTiefen.add(s.tiefe); });
		horizontTiefen.forEach(h => { if (h.tiefe !== undefined) alleTiefen.add(h.tiefe); });

		const sortedTiefen = Array.from(alleTiefen).sort((a, b) => a - b);

		const result: {substrat: any, horizont: any, substratIndex: number | null, substratStripe: boolean, horizontStripe: boolean}[] = [];

		// Tracke, welche Indizes bereits verwendet wurden
		const usedSubstratIndices = new Set<number>();
		const usedHorizontIndices = new Set<number>();

		// Erste Zeile: Immer Index 0 für beide (falls vorhanden)
		const firstSubstrat = substratTiefen.length > 0 ? substratTiefen[0] : null;
		const firstHorizont = horizontTiefen.length > 0 ? horizontTiefen[0] : null;

		if (firstSubstrat || firstHorizont) {
			if (firstSubstrat) usedSubstratIndices.add(0);
			if (firstHorizont) usedHorizontIndices.add(0);

			result.push({
				substrat: firstSubstrat ? substratfolge[0] : null,
				horizont: firstHorizont ? horizontfolge[0] : null,
				substratIndex: firstSubstrat ? 0 : null,
				substratStripe: false,
				horizontStripe: false
			});
		}

		// Restliche Zeilen nach Tiefe sortiert
		for (const tiefe of sortedTiefen) {
			const substrat = substratTiefen.find(s => s.tiefe === tiefe && !usedSubstratIndices.has(s.index));
			const horizont = horizontTiefen.find(h => h.tiefe === tiefe && !usedHorizontIndices.has(h.index));

			// Überspringe, wenn beide null sind
			if (!substrat && !horizont) continue;

			if (substrat) usedSubstratIndices.add(substrat.index);
			if (horizont) usedHorizontIndices.add(horizont.index);

			result.push({
				substrat: substrat ? substratfolge[substrat.index] : null,
				horizont: horizont ? horizontfolge[horizont.index] : null,
				substratIndex: substrat ? substrat.index : null,
				substratStripe: false,
				horizontStripe: false
			});
		}

		// Stripes berechnen: Von unten nach oben
		// Echte Zeilen bekommen abwechselnd Farben, Leerzeilen bekommen die Farbe der echten Zeile DARUNTER
		let currentSubstratStripe = false;
		let currentHorizontStripe = false;

		for (let i = result.length - 1; i >= 0; i--) {
			const row = result[i];

			// Substrat: Erst wechseln bei echter Zeile, dann Farbe zuweisen
			if (row.substrat !== null) {
				currentSubstratStripe = !currentSubstratStripe;
			}
			row.substratStripe = currentSubstratStripe;

			// Horizont: gleiche Logik
			if (row.horizont !== null) {
				currentHorizontStripe = !currentHorizontStripe;
			}
			row.horizontStripe = currentHorizontStripe;
		}

		return result;
	}

	/**
	 * Die Feinbodenform, wie sie auf dem Papier steht: Kuerzel, dann das „(G)"
	 * (reliktischer Grundwassereinfluss), dann das Mesoklima — dieselbe Reihenfolge
	 * wie auf dem Bildschirm (`feinbodenformText`). Bis 20.09.2026 hing das „(G)"
	 * im PDF hinter der Wasserform; dort gehoert es nicht hin (Nutzer-Befund).
	 *
	 * Ohne Feinbodenform gibt es auch kein „(G)": Die blosse Klammer waere kein Wert.
	 * Steht sie EINMAL hier, koennen die beiden Fassungen der Zusammenfassung
	 * (Bohrpunkt-Protokoll und Grube-Formular) nicht auseinanderlaufen.
	 */
	private feinbodenformText(bp: AufnahmePunktDocument): string {
		const fbf = bodenformString(bp?.header?.bodenform, 'fbf') || '';
		const g = fbf && bp?.header?.bodenform?.reliktG ? '(G)' : '';
		const meso = bp?.extendedHeader?.mesoklima ? String(bp.extendedHeader.mesoklima) : '';
		return meso ? `${fbf}${g}_${meso}` : `${fbf}${g}`;
	}

	/** Berechnet nWSK für eine Substratschicht direkt über die Evaluator-Funktion */
	private schichtNWSK(bp: AufnahmePunktDocument, index: number): string {
		const result = schichtNWSKEval(bp, index) as number | undefined;
		if (result === undefined || result === null || isNaN(result)) return '';
		return Math.round(result).toString();
	}

	/** Rendert die Zusammenfassung (HBF, FBF, nWSK, Humusauflage etc.) */
	private renderZusammenfassung(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const h = bp.header;
		const eh = bp.extendedHeader;
		let y = startY;
		// Filz: Saugsaum und Spiegeltiefe wie die Tiefen im Profil ab Oberkante Ol
		// (filz-tiefen.ts); die Karbonattiefe kommt schon verschoben aus dem Auswerter.
		const lage = filzLage(bp);

		y = this.renderSectionHeader(doc, 'Zusammenfassung', margin, y, contentWidth);

		doc.setFontSize(7);
		const lineHeight = 3.5;

		// Humusauflage (Ol/Of/Oh) steht nicht mehr hier, sondern ueber der Horizontfolge-Tabelle
		// (renderHumusAuflage) — die uebrigen Spalten ruecken dafuer nach links auf.
		const colWidth = contentWidth / 11;
		const c1 = margin + 2.5;              // Label 1 (HBF, FBF, HF, St.-STFG), rechts vom Akzentbalken
		const c2 = margin + colWidth;         // Wert 1 (breit, colspan=2)
		const c6 = margin + colWidth * 3;     // Label 2 (Wasserform, Mesoklima, Saugsaum, Spiegeltiefe)
		const c7 = margin + colWidth * 4.2;   // Wert 2
		const c8 = margin + colWidth * 5.2;   // Label 3 (pH, V-Wert, Karbonattiefe)
		const c9 = margin + colWidth * 6.5;   // Wert 3
		const c10 = margin + colWidth * 7.4;  // Label 4 (Entw., nWSK, nWSK-Stufe, Substratfeuchte)
		const c11 = margin + colWidth * 8.7;  // Wert 4 (breiter)

		// Die Standortkennwerte (HBF, FBF, HF, St.-STFG) sind das, was die Foerster lesen wollen
		// (Nutzer-Wunsch 14.09.2026): hell gruen hinterlegt, Akzentbalken links, Werte fett und
		// eine Stufe groesser. Die uebrigen Spalten bleiben, wie sie sind.
		const kastenOben = y - 2.8;
		const kastenHoehe = 4 * lineHeight + 0.6;
		const kastenBreite = c6 - 3 - margin;
		doc.setFillColor(this.tintR, this.tintG, this.tintB);
		doc.rect(margin, kastenOben, kastenBreite, kastenHoehe, 'F');
		doc.setFillColor(this.accentR, this.accentG, this.accentB);
		doc.rect(margin, kastenOben, 1.2, kastenHoehe, 'F');
		const kennwert = (wert: string, zeileY: number) => {
			doc.setFont('helvetica', 'bold');
			doc.setFontSize(8);
			doc.text(wert, c2, zeileY);
			doc.setFont('helvetica', 'normal');
			doc.setFontSize(7);
		};

		// Zeile 1: HBF | Wasserform | pH | Entw.-verhalten
		doc.setFont('helvetica', 'bold');
		doc.text('HBF', c1, y);
		kennwert(bodenformString(h.bodenform, 'hbf') || '', y);

		doc.setFont('helvetica', 'bold');
		doc.text('Wasserform', c6, y);
		doc.setFont('helvetica', 'normal');
		doc.text(wasserformString(bp) || '', c7, y);

		doc.setFont('helvetica', 'bold');
		const ph1Lbl = eh?.ph?.ort1 === 'Saugsaum' ? 'pH(SS)' : 'pH(OB)';
		doc.text(ph1Lbl, c8, y);
		doc.setFont('helvetica', 'normal');
		doc.text(eh?.ph?.wert1?.toString() || '', c9, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Entw.-verh.', c10, y);
		doc.setFont('helvetica', 'normal');
		doc.text(eh?.entwaesserungsverhalten ? String(eh.entwaesserungsverhalten) : '', c11, y);

		y += lineHeight;

		// Zeile 2: FBF | Mesoklima | V-Wert | nWSK
		doc.setFont('helvetica', 'bold');
		doc.text('FBF', c1, y);
		// FBF mit (G) und Mesoklima-Suffix (wie in Komponente)
		const mesoklima = eh?.mesoklima ? String(eh.mesoklima) : '';
		kennwert(this.feinbodenformText(bp), y);

		doc.setFont('helvetica', 'bold');
		doc.text('Mesoklima', c6, y);
		doc.setFont('helvetica', 'normal');
		doc.text(mesoklima, c7, y);

		doc.setFont('helvetica', 'bold');
		doc.text('V-Wert', c8, y);
		doc.setFont('helvetica', 'normal');
		const vWert = eh?.vWertSaugsaum ? eh.vWertSaugsaum.toString() : (eh?.vWertSaugsaumVermutet ? String(eh.vWertSaugsaumVermutet) : '');
		doc.text(vWert, c9, y);

		doc.setFont('helvetica', 'bold');
		doc.text('nWSK', c10, y);
		doc.setFont('helvetica', 'normal');
		doc.text(roundToString(eh?.nWsk, 2) || '', c11, y);

		y += lineHeight;

		// Zeile 3: HF/VegFG | Saugsaum | Karbonattiefe | nWSK-Stufe
		const humusformNeu = h.humusform?.aufnahmeFlaeche?.neu || h.humusform?.aufnahmeFlaeche?.alt || '';
		const hfLbl = isM(bp) ? 'VegFG' : 'HF';
		const hfVal = isM(bp) ? (h.vegetationsformengruppe ? String(h.vegetationsformengruppe) : '') : humusformNeu;

		doc.setFont('helvetica', 'bold');
		doc.text(hfLbl, c1, y);
		kennwert(hfVal, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Saugsaum', c6, y);
		doc.setFont('helvetica', 'normal');
		doc.text(this.tiefeText(eh?.saugsaum, lage), c7, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Karbonattiefe', c8, y);
		doc.setFont('helvetica', 'normal');
		const karbonatiefe = this.getKarbonattiefe(bp);
		doc.text(karbonatiefe, c9, y);

		doc.setFont('helvetica', 'bold');
		doc.text('nWSK-Stufe', c10, y);
		doc.setFont('helvetica', 'normal');
		doc.text(eh?.nWskStufe ? String(eh.nWskStufe) : '', c11, y);

		y += lineHeight;

		// Zeile 4: St.-STFG | | Spiegeltiefe | | Substratfeuchte
		doc.setFont('helvetica', 'bold');
		doc.text('St.-STFG', c1, y);
		kennwert(stfgString(bp), y);

		doc.setFont('helvetica', 'bold');
		doc.text('Spiegeltiefe', c6, y);
		doc.setFont('helvetica', 'normal');
		// Über Flur als „10 cm über Flur", bei Filz ab Oberkante Auflage (flur-tiefe.ts).
		doc.text(flurTiefeText(angezeigteTiefe(eh?.spiegelTiefe, lage.versatz)), c7, y);

		doc.setFont('helvetica', 'bold');
		doc.text('Substratfeuchte', c10, y);
		doc.setFont('helvetica', 'normal');
		doc.text(h.substratFeuchte ? String(h.substratFeuchte) : '', c11, y);

		y += lineHeight;

		// ein Millimeter Luft zwischen dem hinterlegten Kasten und der naechsten Ueberschrift
		return y + 1;
	}

	/**
	 * Karbonattiefe = Oberkante der ersten karbonathaltigen Substratschicht
	 * (0, wenn schon die erste Schicht Karbonat enthält). Delegiert an den
	 * Evaluator, damit Protokoll und Eingabemaske denselben Wert zeigen.
	 */
	private getKarbonattiefe(bp: AufnahmePunktDocument): string {
		const v = karbonatEval(bp) as number | undefined;
		return v === undefined ? '' : v.toString();
	}

	/** Rendert das Kurzprotokoll für Punkte ohne vollständiges Protokoll */
	private renderKurzprotokoll(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const h = bp.header;
		let y = startY;

		doc.setFontSize(13);
		doc.setFont('helvetica', 'bold');
		doc.text('Aufnahmepunkt ' + this.punktTitel(bp), margin, y);
		y += 1.5;
		doc.setDrawColor(this.accentR, this.accentG, this.accentB);
		doc.setLineWidth(0.5);
		doc.line(margin, y, margin + contentWidth, y);
		doc.setLineWidth(0.2);
		y += 5;

		doc.setFontSize(9);
		const col1 = margin;
		const col2 = margin + 28;
		const lineHeight = 5;

		doc.setFont('helvetica', 'bold');
		doc.text('Punktart:', col1, y);
		doc.setFont('helvetica', 'normal');
		doc.text(this.shortenPunktart(bp.type), col2, y);
		y += lineHeight;

		doc.setFont('helvetica', 'bold');
		doc.text('Datum:', col1, y);
		doc.setFont('helvetica', 'normal');
		doc.text(this.formatDate(h.aufnahme?.datum), col2, y);
		y += lineHeight;

		doc.setFont('helvetica', 'bold');
		doc.text('Kartierer:', col1, y);
		doc.setFont('helvetica', 'normal');
		doc.text(h.aufnahme?.kartierer || '', col2, y);
		y += lineHeight;

		if (h.koordinaten) {
			doc.setFont('helvetica', 'bold');
			doc.text('Koordinaten:', col1, y);
			doc.setFont('helvetica', 'normal');
			doc.text(`${String(h.koordinaten.x)} / ${String(h.koordinaten.y)}`, col2, y);
			y += lineHeight;
		}

		if (h.bemerkungen) {
			doc.setFont('helvetica', 'bold');
			doc.text('Bemerkungen:', col1, y);
			y += lineHeight;
			doc.setFont('helvetica', 'normal');
			const bemLines = doc.splitTextToSize(h.bemerkungen, contentWidth - 5);
			doc.text(bemLines, col1 + 2, y);
			y += bemLines.length * lineHeight;
		}

		return y;
	}

	/** Baut Relief-String (identisch mit reliefBeschreibung() in bohrpunkt-allgemein) */
	private buildReliefString(bp: AufnahmePunktDocument): string {
		const relief = bp.extendedHeader?.relief;
		if (!relief) return '';

		if (isM(bp)) {
			return [relief.reliefBestand, relief.reliefBodenvegetation].filter(v => !!v).join(' / ');
		}

		const parts: string[] = [];

		// Teil 1: Neigung + Hangteil + Einbindung/Hanganschluss
		let neigungTeil = '';
		if (relief.neigungsstufe) {
			neigungTeil = relief.neigungsstufe;
			if (relief.hangteil) {
				neigungTeil = neigungTeil.replace(/hang$/, `-${relief.hangteil}`);
			}
		} else if (relief.hangteil) {
			neigungTeil = relief.hangteil;
		}

		if (relief.einbindungEbene) {
			neigungTeil = neigungTeil ? `${neigungTeil} in ${relief.einbindungEbene}` : relief.einbindungEbene;
		} else if (relief.hanganschluss) {
			let anschlussTeil = relief.hanganschluss;
			if (relief.hoehenspanne) anschlussTeil += ` (${relief.hoehenspanne} m)`;
			neigungTeil = neigungTeil ? `${neigungTeil} in ${anschlussTeil}` : anschlussTeil;
		} else if (relief.hoehenspanne && neigungTeil) {
			neigungTeil += ` (${relief.hoehenspanne} m)`;
		}

		if (neigungTeil) parts.push(neigungTeil);

		// Teil 2: Wölbung (verbal)
		if (relief.woelbung) {
			const woelbungMap: Record<string, string> = {
				'xx': 'doppelt konvex', 'xg': 'konvex-gestreckt', 'xv': 'konvex-konkav',
				'gx': 'gestreckt-konvex', 'gg': 'doppelt gestreckt', 'gv': 'gestreckt-konkav',
				'vx': 'konkav-konvex', 'vg': 'konkav-gestreckt', 'vv': 'doppelt konkav'
			};
			parts.push(woelbungMap[relief.woelbung] || relief.woelbung);
		}

		// Teil 3: Hangrichtung
		if (relief.hangrichtung) parts.push(`${relief.hangrichtung}-gerichtet`);

		let result = parts.join(', ');

		// Teil 4: Innere Rauhheit (nach Semikolon)
		if (relief.innereRauhheit) {
			result = result ? `${result}; ${relief.innereRauhheit}` : relief.innereRauhheit;
		}

		// Fallback: Wenn keine neuen Felder gesetzt sind, Legacy-Relief anzeigen
		if (!result) {
			result = [relief.relief, relief.reliefUmgebung].filter(v => !!v).join(' / ');
		}

		return result;
	}

	/** Rendert die Vegetationsaufnahme */
	/**
	 * Vegetationsaufnahme: Schichtanteile und die Artenliste.
	 *
	 * Die Liste blieb bis zum 25.09.2026 ohne jeden Blick auf das Seitenende:
	 * Sie schrieb sich ueber den Abkuerzungs-Fusstext, und was unter den Rand
	 * geriet, fehlte im PDF ganz (Nutzer-Befund). Jetzt entscheidet
	 * `vegetationLayout` vorab, ob der Abschnitt hier oder auf einer neuen Seite
	 * steht und ob er ein- oder zweispaltig gesetzt wird; alles bleibt ueber dem
	 * Fusstext (`fussOberkante`), und wo es auch zweispaltig nicht reicht,
	 * brechen die Zeilen um, mit wiederholter Kopfzeile.
	 */
	private renderVegetation(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const veg = bp.bodenvegetation;
		const vegetationen = veg?.aufnahme?.vegetationen || [];
		if (!vegetationen.length && !this.hasSchichtAnteile(veg)) {
			return startY; // Keine Vegetation vorhanden
		}

		const rowHeight = 3.5;
		const mitAnteilen = this.hasSchichtAnteile(veg);
		// Vegetationen nach Schicht gruppieren und sortieren
		const zeilen = this.sortVegetationen(vegetationen);

		// Die Grenze: 2 mm ueber dem Fusstext. Masse der Bausteine wie unten gezeichnet.
		const fussOben = this.fussOberkante(doc, bp) - 2;
		const kopfHoehe = 9;                             // renderSectionHeader
		const anteileHoehe = mitAnteilen ? 5 + rowHeight + 1 : 0;
		const tabellenKopf = zeilen.length ? 4.5 : 0;    // Kopfzeile + Linie bis zur ersten Grundlinie
		const spaltenAbstand = 4;
		const spaltenBreite = (contentWidth - spaltenAbstand) / 2;
		const zwei = zeilen.length >= 2 ? this.vegetationZweiSpalten(doc, zeilen, spaltenBreite, rowHeight) : null;
		const lage = vegetationLayout({
			einspaltig: kopfHoehe + anteileHoehe + tabellenKopf + zeilen.length * rowHeight,
			zweispaltig: zwei ? kopfHoehe + anteileHoehe + tabellenKopf + zwei.hoehe : Infinity,
			platzHier: fussOben - startY,
			platzFrisch: fussOben - this.marginTop,
			mindestens: kopfHoehe + anteileHoehe + tabellenKopf + 3 * rowHeight,
		});

		let y = startY;
		if (lage.neueSeite) {
			doc.addPage();
			y = this.marginTop;
		}

		// Überschrift
		y = this.renderSectionHeader(doc, 'Vegetationsaufnahme', margin, y, contentWidth);

		// Schichtanteile
		if (mitAnteilen) {
			doc.setFontSize(7);
			doc.setFillColor(220, 220, 220);
			doc.rect(margin, y - 2.5, contentWidth, 3.5, 'F');
			doc.text('Schichtanteile', margin + 1, y);
			y += 5;

			doc.setFont('helvetica', 'normal');
			doc.setFontSize(7);
			const anteile = veg.aufnahme.schichtAnteile;
			const schichten: ('b1' | 'b2' | 'str' | 'kr1' | 'kr2' | 'm')[] = ['b1', 'b2', 'str', 'kr1', 'kr2', 'm'];
			const vorhandene = schichten.filter(sch => anteile[sch] !== undefined && anteile[sch] !== null);
			const colWidth = vorhandene.length > 1 ? contentWidth / vorhandene.length : contentWidth;
			let xPos = margin + 1;

			vorhandene.forEach(sch => {
				const label = schichtLabel(sch) + ': ';
				doc.setFont('helvetica', 'bold');
				doc.text(label, xPos, y);
				const labelWidth = doc.getTextWidth(label);
				doc.setFont('helvetica', 'normal');
				doc.text(anteile[sch] + ' %', xPos + labelWidth, y);
				xPos += colWidth;
			});
			y += rowHeight + 1;
		}

		// Vegetationstabelle
		if (!zeilen.length) return y;

		// Hinterlegt wird je Schicht, nicht je Zeile: Die Zeilen EINER Schicht stehen auf
		// demselben Grund, erst die naechste Schicht wechselt (Nutzer-Wunsch 14.09.2026).
		// Der erste Block bleibt weiss - bei nur einer Schicht gibt es dann nichts zu
		// unterscheiden und auch keinen grauen Klotz.
		const bloecke: number[] = [];
		let block = -1;
		zeilen.forEach((z, i) => {
			if (i === 0 || z.schicht !== zeilen[i - 1].schicht) block++;
			bloecke.push(block);
		});

		// Kopfzeile einer Tabelle (die ganze Breite oder eine Spalte); gibt die
		// Grundlinie der ersten Zeile zurueck.
		const tabellenKopfZeichnen = (x: number, breite: number, nameX: number, yy: number): number => {
			doc.setFontSize(7);
			doc.setFont('helvetica', 'bold');
			doc.setFillColor(220, 220, 220);
			doc.rect(x, yy - 2.5, breite, 3.5, 'F');
			doc.text('Schicht', x + 1, yy);
			doc.text('Vegetation', x + nameX, yy);
			doc.text('%', x + breite - 2, yy, { align: 'right' }); // Rechtsbündig mit Abstand vom Rand
			yy += 1;
			doc.setDrawColor(100);
			doc.line(x, yy, x + breite, yy);
			doc.setFont('helvetica', 'normal');
			return yy + rowHeight;
		};

		// Eine Zeile. Die Schicht steht nur in der ersten Zeile ihres Blocks - und
		// noch einmal, wo ein Block nach Umbruch oder Spaltenwechsel weitergeht.
		const zeileZeichnen = (i: number, x: number, breite: number, nameX: number, yy: number, hoehe: number, schichtZeigen: boolean) => {
			const v = zeilen[i];
			if (bloecke[i] % 2 === 1) {
				doc.setFillColor(245, 245, 245);
				doc.rect(x, yy - 2.5, breite, hoehe, 'F');
			}
			if (schichtZeigen) doc.text(v.schicht ? schichtLabel(v.schicht) : '', x + 1, yy);
			doc.text(doc.splitTextToSize(v.vegetation || '', breite - nameX - 10) as string[], x + nameX, yy);
			// haeufigkeit kann string oder number sein, rechtsbündig mit Abstand
			const haeufStr = v.haeufigkeit !== undefined && v.haeufigkeit !== null ? String(v.haeufigkeit) : '';
			doc.text(haeufStr, x + breite - 2, yy, { align: 'right' });
		};
		const unterlinie = (x: number, breite: number, yy: number, letzteHoehe: number) => {
			doc.setDrawColor(150);
			doc.line(x, yy - letzteHoehe + 2.5, x + breite, yy - letzteHoehe + 2.5);
		};

		if (lage.spalten === 1 || !zwei) {
			const nameX = 25;
			y = tabellenKopfZeichnen(margin, contentWidth, nameX, y);
			let nachUmbruch = false;
			for (let i = 0; i < zeilen.length; i++) {
				if (y - 2.5 + rowHeight > fussOben) {
					// Seitenende: neue Seite, Ueberschrift und Kopfzeile noch einmal.
					doc.addPage();
					y = this.marginTop;
					y = this.renderSectionHeader(doc, 'Vegetationsaufnahme (Forts.)', margin, y, contentWidth);
					y = tabellenKopfZeichnen(margin, contentWidth, nameX, y);
					nachUmbruch = true;
				}
				const neu = i === 0 || bloecke[i] !== bloecke[i - 1];
				zeileZeichnen(i, margin, contentWidth, nameX, y, rowHeight, neu || nachUmbruch);
				nachUmbruch = false;
				y += rowHeight;
			}
			unterlinie(margin, contentWidth, y, rowHeight);
			return y;
		}

		// Zweispaltig: links und rechts je eine halbe Tabelle mit derselben
		// Kopfzeile; die Liste laeuft links von oben nach unten und rechts weiter.
		const nameX = 24;
		const xLinks = margin;
		const xRechts = margin + spaltenBreite + spaltenAbstand;
		const yKopf = y;
		y = tabellenKopfZeichnen(xLinks, spaltenBreite, nameX, y);
		tabellenKopfZeichnen(xRechts, spaltenBreite, nameX, yKopf);
		let yEnde = y;
		for (const [teil, x] of [[zwei.links, xLinks], [zwei.rechts, xRechts]] as const) {
			let yy = y;
			let letzteHoehe = rowHeight;
			teil.forEach((t, k) => {
				const neu = k === 0 || bloecke[t.index] !== bloecke[t.index - 1];
				zeileZeichnen(t.index, x, spaltenBreite, nameX, yy, t.hoehe, neu);
				yy += t.hoehe;
				letzteHoehe = t.hoehe;
			});
			unterlinie(x, spaltenBreite, yy, letzteHoehe);
			yEnde = Math.max(yEnde, yy);
		}
		return yEnde;
	}

	/**
	 * Teilt die Zeilen fuer zwei Spalten: links von oben nach unten, rechts
	 * weiter, beide etwa gleich hoch. In der halben Breite kann ein Artname
	 * umbrechen, die Zeile wird dann hoeher - gemessen mit der Schrift, in der
	 * gezeichnet wird.
	 */
	private vegetationZweiSpalten(
		doc: jsPDF, zeilen: BodenvegetationZeile[], spaltenBreite: number, rowHeight: number,
	): { links: SpaltenZeile[]; rechts: SpaltenZeile[]; hoehe: number } {
		doc.setFontSize(7);
		doc.setFont('helvetica', 'normal');
		const nameBreite = spaltenBreite - 24 - 10;
		const alle: SpaltenZeile[] = zeilen.map((z, index) => {
			const zeilenZahl = (doc.splitTextToSize(z.vegetation || '', nameBreite) as string[]).length;
			return { index, hoehe: rowHeight + Math.max(0, zeilenZahl - 1) * 2.9 };
		});
		const gesamt = alle.reduce((s, z) => s + z.hoehe, 0);
		let summe = 0;
		let schnitt = alle.length - 1;
		for (let i = 0; i < alle.length - 1; i++) {
			summe += alle[i].hoehe;
			if (summe >= gesamt / 2) {
				schnitt = i + 1;
				break;
			}
		}
		const hoeheVon = (teil: SpaltenZeile[]) => teil.reduce((s, z) => s + z.hoehe, 0);
		const links = alle.slice(0, schnitt);
		const rechts = alle.slice(schnitt);
		return { links, rechts, hoehe: Math.max(hoeheVon(links), hoeheVon(rechts)) };
	}

	/** Prüft ob Schichtanteile vorhanden sind */
	private hasSchichtAnteile(veg: any): boolean {
		if (!veg?.aufnahme?.schichtAnteile) return false;
		const a = veg.aufnahme.schichtAnteile;
		return !!(a.b1 || a.b2 || a.str || a.kr1 || a.kr2 || a.m);
	}

	/** Sortiert Vegetationen primär nach Schicht (Reihenfolge b1, b2, str, kr1, kr2, m), sekundär nach Häufigkeit absteigend. */
	private sortVegetationen(vegetationen: BodenvegetationZeile[]): BodenvegetationZeile[] {
		const order: Record<string, number> = { 'b1': 1, 'b2': 2, 'str': 3, 'kr1': 4, 'kr2': 5, 'm': 6 };
		const haeufNum = (h: string | number | undefined | null): number => {
			if (h === undefined || h === null || h === '') return -Infinity;
			const n = typeof h === 'number' ? h : parseFloat(String(h));
			return isNaN(n) ? -Infinity : n;
		};
		return [...vegetationen].sort((a, b) => {
			const aOrder = a.schicht ? (order[a.schicht] || 99) : 99;
			const bOrder = b.schicht ? (order[b.schicht] || 99) : 99;
			if (aOrder !== bOrder) return aOrder - bOrder;
			return haeufNum(b.haeufigkeit) - haeufNum(a.haeufigkeit);
		});
	}

	/** Die Abkuerzungen im Fusstext - Moore haben drei mehr. */
	private fussZeilen(bp: AufnahmePunktDocument): string[] {
		const isMoor = isM(bp);
		const abbreviations: string[] = [
			'HBF = Hauptbodenform',
			'FBF = Feinbodenform',
			isMoor ? 'VegFG = Vegetationsformengruppe' : 'HF = Humusform',
			'St.-STFG = Stamm-Standortformengruppe',
			'nWSK = nutzbare Wasserspeicherkapazität',
			'Ol/Of/Oh = Humusauflage (Streu/Fermentation/Humifizierung)',
			'LagD = Lagerungsdichte',
			'Skel = Skelettgehalt',
			'Karb = Karbonatgehalt',
			'Mächt = Mächtigkeit',
			'W = Wechselschichtung',
		];

		if (isMoor) {
			abbreviations.push(
				'Zers = Zersetzungsgrad',
				'Kons = Konsistenz',
				'SubV = Substanzvolumen',
			);
		}
		return abbreviations;
	}

	/** Zeilenabstand und Spaltenzahl des Fusstextes; die Hoehe folgt daraus. */
	private readonly fussZeilenHoehe = 2.5;
	private fussSpalten(bp: AufnahmePunktDocument): number {
		return isM(bp) ? 3 : 2;
	}

	/** Hoehe des Fusstextes samt Trennlinie (mm). */
	private fussHoehe(bp: AufnahmePunktDocument): number {
		return Math.ceil(this.fussZeilen(bp).length / this.fussSpalten(bp)) * this.fussZeilenHoehe + 3;
	}

	/** Wo der Fusstext beginnt (seine Trennlinie) - darueber muss der Inhalt bleiben. */
	private fussOberkante(doc: jsPDF, bp: AufnahmePunktDocument): number {
		return doc.internal.pageSize.getHeight() - this.marginBottom - this.fussHoehe(bp);
	}

	/**
	 * Rendert die Abkuerzungslegende am Seitenende.
	 * @param inhaltEnde bis wohin der Inhalt auf dieser Seite reicht. Liegt er
	 *   schon im Fusstext, kommt der auf eine neue Seite - nichts wird
	 *   uebereinandergeschrieben (Nutzer-Befund 25.09.2026).
	 */
	private renderFooter(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, contentWidth: number, inhaltEnde: number): void {
		const abbreviations = this.fussZeilen(bp);
		const colCount = this.fussSpalten(bp);
		const rowsPerCol = Math.ceil(abbreviations.length / colCount);
		const colWidth = contentWidth / colCount;

		if (inhaltEnde > this.fussOberkante(doc, bp)) {
			doc.addPage();
		}

		doc.setFontSize(5.5);
		doc.setFont('helvetica', 'normal');
		doc.setTextColor(140, 140, 140);

		const lineHeight = this.fussZeilenHoehe;
		let y = this.fussOberkante(doc, bp);

		// Feine Trennlinie
		doc.setDrawColor(180, 180, 180);
		doc.setLineWidth(0.15);
		doc.line(margin, y, margin + contentWidth, y);
		doc.setLineWidth(0.2);
		y += 3;

		for (let col = 0; col < colCount; col++) {
			const startIdx = col * rowsPerCol;
			const endIdx = Math.min(startIdx + rowsPerCol, abbreviations.length);
			let rowY = y;
			for (let i = startIdx; i < endIdx; i++) {
				doc.text(abbreviations[i], margin + col * colWidth, rowY);
				rowY += lineHeight;
			}
		}

		doc.setTextColor(0, 0, 0);
	}

	// ======================================================================
	// Grube-Formular (SEA-95 „Weisergrubenaufnahme", Punktart 2 + 4)
	// ======================================================================

	/** Seitenumbruch, wenn der naechste Block nicht mehr passt. Gibt das (ggf. neue) y zurueck. */
	private ensureSpace(doc: jsPDF, y: number, needed: number): number {
		const pageHeight = doc.internal.pageSize.getHeight();
		if (y + needed > pageHeight - this.marginBottom) {
			doc.addPage();
			return this.marginTop;
		}
		return y;
	}

	/** Noetige Hoehe einer Formularzelle (kleines Label + umgebrochener Wert), etwas luftiger. */
	private formCellHeight(doc: jsPDF, w: number, value: string): number {
		doc.setFontSize(7.5);
		const lines = value ? doc.splitTextToSize(value, w - 2) : [''];
		return 3 + Math.max(lines.length, 1) * 3.4 + 2.6;
	}

	/** Zeichnet eine beschriftete Formularzelle (Rahmen, graues Label oben, Wert darunter). */
	private formCell(doc: jsPDF, x: number, y: number, w: number, h: number, label: string, value: string): void {
		doc.setDrawColor(170);
		doc.setLineWidth(0.2);
		doc.rect(x, y, w, h);
		doc.setFontSize(5.2);
		doc.setFont('helvetica', 'normal');
		doc.setTextColor(120, 120, 120);
		doc.text(label, x + 1, y + 2.8);
		doc.setTextColor(0, 0, 0);
		if (value) {
			doc.setFontSize(7.5);
			doc.text(doc.splitTextToSize(value, w - 2), x + 1, y + 6, { lineHeightFactor: 1.25 });
		}
	}

	/** Eine Kopf-Sektion als beschriftetes Kaestchen-Raster (Default 3 Gewichtseinheiten je Zeile). */
	private renderFormSektion(doc: jsPDF, sektion: FormSektion, x: number, startY: number, totalW: number, cols = 3): number {
		let y = this.renderSectionHeader(doc, sektion.titel, x, startY, totalW);
		const COLS = cols;
		const unitW = totalW / COLS;

		// Felder gewichtet in Zeilen packen
		const rows: FormFeld[][] = [];
		let cur: FormFeld[] = [];
		let curW = 0;
		for (const f of sektion.felder) {
			const w = Math.min(f.w || 1, COLS);
			if (curW + w > COLS) { rows.push(cur); cur = []; curW = 0; }
			cur.push(f);
			curW += w;
		}
		if (cur.length) rows.push(cur);

		for (const row of rows) {
			const heights = row.map(f => this.formCellHeight(doc, Math.min(f.w || 1, COLS) * unitW, f.value));
			const rowH = Math.max(...heights, 7);
			y = this.ensureSpace(doc, y, rowH);
			let cx = x;
			for (const f of row) {
				const cw = Math.min(f.w || 1, COLS) * unitW;
				this.formCell(doc, cx, y, cw, rowH, f.label, f.value);
				cx += cw;
			}
			y += rowH;
		}
		return y + 4;
	}

	/**
	 * Generische Tabelle im Formular-Gitter-Stil (vollstaendiges Zellraster,
	 * blassgruener Spaltenkopf — passend zu den gruenen Sektionsbalken und den
	 * Kaestchen der Vorderseite). Mit Seitenumbruch (Kopf wird neu gezeichnet).
	 */
	private renderColumnTable<T>(doc: jsPDF, x: number, startY: number, totalW: number, title: string, cols: PdfCol<T>[], rows: T[], nachZeile?: NachZeile): number {
		const wSum = cols.reduce((a, c) => a + c.w, 0) || 1;
		const colW = cols.map(c => (c.w / wSum) * totalW);
		const colX: number[] = [];
		let cx = x;
		for (let i = 0; i < cols.length; i++) { colX.push(cx); cx += colW[i]; }
		const tableEndX = x + totalW;
		const boundaries = [...colX, tableEndX];
		const rowH = 3.6;

		// Senkrechte Gitterlinien fuer eine Zeilen-/Kopf-Bande
		const drawVerticals = (yTop: number, yBot: number) => {
			doc.setDrawColor(205);
			doc.setLineWidth(0.15);
			boundaries.forEach(bx => doc.line(bx, yTop, bx, yBot));
		};

		// Kopfzeile: blassgruener Streifen, fette Beschriftung, Rahmen. Gibt die
		// Grundlinie der ersten Datenzeile zurueck (nahtlos an den Kopf anschliessend).
		const drawHeader = (yy: number): number => {
			const top = yy - 2.5;
			const bot = top + 4;
			doc.setFillColor(225, 235, 225);
			doc.rect(x, top, totalW, 4, 'F');
			doc.setFontSize(6);
			doc.setFont('helvetica', 'bold');
			doc.setTextColor(0, 0, 0);
			cols.forEach((c, i) => {
				if (c.align === 'r') doc.text(c.header, colX[i] + colW[i] - 1, yy, { align: 'right' });
				else doc.text(c.header, colX[i] + 1, yy);
			});
			doc.setDrawColor(120);
			doc.setLineWidth(0.2);
			doc.line(x, top, tableEndX, top);
			doc.line(x, bot, tableEndX, bot);
			drawVerticals(top, bot);
			return bot + 2.5;
		};

		let y = this.renderSectionHeader(doc, title, x, startY, totalW);
		y = drawHeader(y);
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(6.5);

		const pageHeight = doc.internal.pageSize.getHeight();
		rows.forEach((row, idx) => {
			const cellLines = cols.map((c, i) => {
				const v = c.get(row, idx) || '';
				return (v ? doc.splitTextToSize(v, colW[i] - 2) : ['']) as string[];
			});
			const lineCount = Math.max(...cellLines.map(l => l.length), 1);
			const thisH = Math.max(rowH, lineCount * 3 + 0.8);

			if (y - 2.5 + thisH > pageHeight - this.marginBottom) {
				doc.addPage();
				y = this.marginTop;
				y = this.renderSectionHeader(doc, title + ' (Forts.)', x, y, totalW);
				y = drawHeader(y);
				doc.setFont('helvetica', 'normal');
				doc.setFontSize(6.5);
			}

			const bandTop = y - 2.5;
			const bandBot = bandTop + thisH;
			doc.setTextColor(0, 0, 0);
			cols.forEach((c, i) => {
				if (c.align === 'r') doc.text(cellLines[i], colX[i] + colW[i] - 1, y, { align: 'right' });
				else doc.text(cellLines[i], colX[i] + 1, y);
			});
			drawVerticals(bandTop, bandBot);
			doc.setDrawColor(205);
			doc.setLineWidth(0.15);
			doc.line(x, bandBot, tableEndX, bandBot);
			// Nachtrag der aufrufenden Tabelle (heute: die Wechsel-Klammer der
			// Substratfolge). Steht NACH den Gitterlinien, damit sie nicht
			// darueber liegen.
			nachZeile?.(idx, bandTop, thisH, colX, colW);
			y += thisH;
		});

		return y + 1;
	}

	/**
	 * Haupteinstieg: rendert die komplette Grube als formular-getreues Mehrseiten-PDF.
	 * Gibt die (1-basierte) Seitennummer zurück, ab der das Querformat (Bodenprofil)
	 * beginnt — wird für die laufende Kopfzeile (Hoch-/Querformat-Breite) gebraucht.
	 */
	private renderGrubeForm(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, contentWidth: number): void {
		// Abstand zur laufenden Kopfzeile (wird später bei y≈7–9 gestempelt), damit der große Titel nicht anstößt.
		let y = this.marginTop + 5;

		// Titelband
		doc.setFontSize(13);
		doc.setFont('helvetica', 'bold');
		const formTitel = idOf(bp.type) === 2 ? 'Weisergrubenaufnahme' : 'Bodengrubenaufnahme';
		doc.text('SEA 95 · ' + formTitel, margin, y);
		y += 2.5;
		doc.setDrawColor(this.accentR, this.accentG, this.accentB);
		doc.setLineWidth(0.5);
		doc.line(margin, y, margin + contentWidth, y);
		doc.setLineWidth(0.2);
		y += 7;

		// Vorderseite: Kopf-Sektionen (Kaestchen-Raster)
		const sektionen = grubeKopfSektionen(bp, { relief: this.buildReliefString(bp) });
		for (const s of sektionen) {
			y = this.ensureSpace(doc, y, 16);
			y = this.renderFormSektion(doc, s, margin, y, contentWidth);
		}

		// Bestand + Vegetationsaufnahme
		y = this.renderBestandSection(doc, bp, margin, y, contentWidth);
		this.renderGrubeVegetation(doc, bp, margin, y, contentWidth);

		// Rueckseite: Bodenprofil im Querformat (mehr Spalten passen)
		doc.addPage('a4', 'landscape');
		const landW = doc.internal.pageSize.getWidth() - margin - this.marginRight;
		let ly = this.marginTop;
		ly = this.renderGrubeProfil(doc, bp, margin, ly, landW);
		ly = this.ensureSpace(doc, ly + 2, 28);
		ly = this.renderGrubeZusammenfassung(doc, bp, margin, ly, landW);
		this.renderGrubeBemerkungen(doc, bp, margin, ly, landW);
	}

	/** Laufende Kopfzeile oben auf jeder Grube-Seite: Nr. · Datum · Bearbeiter · „Seite X von Y". */
	private drawGrubeRunningHeader(doc: jsPDF, bp: AufnahmePunktDocument, pageNo: number, pageCount: number, pageW: number): void {
		const margin = this.marginLeft;
		const right = pageW - this.marginRight;
		const y = 7;
		const grubeArt = idOf(bp.type) === 2 ? 'Weisergrube' : 'Bodengrube';
		const info = [
			bp.header?.nummer ? `${grubeArt} Nr. ${bp.header.nummer}` : grubeArt,
			this.formatDate(bp.header?.aufnahme?.datum),
			bp.header?.aufnahme?.kartierer || '',
		].filter(Boolean).join('   ·   ');

		doc.setFontSize(7);
		doc.setFont('helvetica', 'normal');
		doc.setTextColor(90, 90, 90);
		doc.text(info, margin, y);
		doc.text(`Seite ${pageNo} von ${pageCount}`, right, y, { align: 'right' });
		doc.setDrawColor(200);
		doc.setLineWidth(0.2);
		doc.line(margin, y + 1.6, right, y + 1.6);
		doc.setTextColor(0, 0, 0);
	}

	/** Zusammenfassung/Auswertung als beschriftetes Kaestchen-Raster (gleicher Stil wie die Vorderseite). */
	private renderGrubeZusammenfassung(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, totalW: number): number {
		const h = bp.header;
		const eh = bp.extendedHeader;
		const ph1Lbl = eh?.ph?.ort1 === 'Saugsaum' ? 'pH (Saugsaum)' : 'pH (Oberboden)';
		const vWert = eh?.vWertSaugsaum ? String(eh.vWertSaugsaum) : (eh?.vWertSaugsaumVermutet ? String(eh.vWertSaugsaumVermutet) : '');
		const sektion: FormSektion = {
			titel: 'Zusammenfassung / Auswertung',
			felder: [
				{ label: 'HBF', value: bodenformString(h?.bodenform, 'hbf') || '', w: 2 },
				{ label: 'FBF', value: this.feinbodenformText(bp), w: 2 },
				{ label: 'Humusform', value: h?.humusform?.aufnahmeFlaeche?.neu || h?.humusform?.aufnahmeFlaeche?.alt || '' },
				{ label: 'St.-STFG', value: eh?.stfg ? stfgString(bp) : '' },
				{ label: 'Ol', value: de(eh?.humusAuflage?.ol) },
				{ label: 'Of', value: de(eh?.humusAuflage?.of) },
				{ label: 'Oh', value: de(eh?.humusAuflage?.oh) },
				{ label: 'Wasserform', value: h ? wasserformString(bp) : '' },
				// Über Flur zählt bei Filz ab Oberkante Auflage (flur-tiefe.ts) - deshalb hier
				// mit der Auflage, anders als die übrigen Tiefen dieses Formulars.
				{ label: 'Spiegeltiefe', value: flurTiefeText(angezeigteTiefe(eh?.spiegelTiefe, filzVersatz(bp))) },
				{ label: 'Saugsaum', value: de(eh?.saugsaum) },
				{ label: ph1Lbl, value: de(eh?.ph?.wert1) },
				{ label: 'V-Wert', value: vWert },
				{ label: 'Karbonattiefe', value: this.getKarbonattiefe(bp) },
				{ label: 'nWSK', value: roundToString(eh?.nWsk, 2) || '' },
				{ label: 'nWSK-Stufe', value: eh?.nWskStufe ? String(eh.nWskStufe) : '' },
				{ label: 'Substratfeuchte', value: h?.substratFeuchte ? String(h.substratFeuchte) : '' },
				{ label: 'Entw.-verhalten', value: eh?.entwaesserungsverhalten ? String(eh.entwaesserungsverhalten) : '' },
			],
		};
		// Querformat: mehr Spalten, damit die Kaestchen aehnlich breit sind wie auf der Vorderseite
		return this.renderFormSektion(doc, sektion, margin, startY, totalW, 5);
	}

	/** Bestand als Baumarten-Tabelle (entfaellt, wenn keine strukturierten Baumarten vorliegen). */
	private renderBestandSection(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const rows = baumartenRows(bp.extendedHeader?.baumarten);
		if (rows.length === 0) return startY;
		const y = this.ensureSpace(doc, startY, 18);
		const cols: PdfCol<BaumartRow>[] = [
			{ header: 'Baumart', w: 24, get: r => r.art },
			{ header: 'Alter [J]', w: 11, align: 'r', get: r => r.alter },
			{ header: 'Höhe [m]', w: 18, get: r => r.hoehe },
			{ header: 'Durchm. [cm]', w: 18, get: r => r.durchmesser },
			{ header: 'Bonität', w: 12, get: r => r.bonitaet },
			{ header: 'Qualität', w: 17, get: r => r.qualitaet },
		];
		return this.renderColumnTable(doc, margin, y, contentWidth, 'Baumarten', cols, rows);
	}

	/** Vegetationsaufnahme tabellarisch: oben Schichtenübersicht (Deckungsgrad je Schicht), darunter Arten je Schicht. */
	private renderGrubeVegetation(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, contentWidth: number): number {
		const veg = bp.bodenvegetation;
		const gruppen = vegetationGruppen(veg);
		const anteile = veg?.aufnahme?.schichtAnteile;
		const groesse = veg?.groesseDerAufnahmeFlaeche;
		const hasAnteile = !!(anteile && (anteile.b1 || anteile.b2 || anteile.str || anteile.kr1 || anteile.kr2 || anteile.m));
		if (gruppen.length === 0 && !hasAnteile && !groesse) return startY;

		let y = this.ensureSpace(doc, startY, 24);

		// Schichtenübersicht (Deckungsgrad je Schicht) als einzeilige Tabelle
		if (hasAnteile || groesse) {
			const overviewCols: PdfCol<unknown>[] = [
				{ header: 'B1', w: 1, align: 'r', get: () => de(anteile?.b1) },
				{ header: 'B2', w: 1, align: 'r', get: () => de(anteile?.b2) },
				{ header: 'Str', w: 1, align: 'r', get: () => de(anteile?.str) },
				{ header: 'Kr1', w: 1, align: 'r', get: () => de(anteile?.kr1) },
				{ header: 'Kr2', w: 1, align: 'r', get: () => de(anteile?.kr2) },
				{ header: 'M', w: 1, align: 'r', get: () => de(anteile?.m) },
				{ header: 'Größe der Aufnahmefläche [m²]', w: 3, get: () => groesse || '' },
			];
			y = this.renderColumnTable(doc, margin, y, contentWidth, 'Vegetationsaufnahme – Schichtenübersicht (Deckungsgrad %)', overviewCols, [{}]);
		}

		// Arten je Schicht als Tabelle (Schicht | Vegetation | Deckung)
		if (gruppen.length > 0) {
			type VegRow = { schicht: string; vegetation: string; deckung: string };
			const detailRows: VegRow[] = [];
			for (const g of gruppen) {
				g.items.forEach((it, i) => detailRows.push({ schicht: i === 0 ? (g.kurz || g.label) : '', vegetation: it.name, deckung: it.deckung }));
			}
			const detailCols: PdfCol<VegRow>[] = [
				{ header: 'Schicht', w: 12, get: r => r.schicht },
				{ header: 'Vegetation', w: 72, get: r => r.vegetation },
				{ header: 'Deckung %', w: 16, align: 'r', get: r => r.deckung },
			];
			y = this.ensureSpace(doc, y + 2, 16);
			y = this.renderColumnTable(doc, margin, y, contentWidth, 'Vegetationsaufnahme – Arten je Schicht', detailCols, detailRows);
		}
		return y;
	}

	/** Bodenprofil: breite Substrat-Tabelle (alle Grube-Spalten) + Horizontfolge. */
	private renderGrubeProfil(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, totalW: number): number {
		let y = startY;
		const sub = bp.Substratfolge || [];
		const subCols: PdfCol<SubstratZeile>[] = [
			{ header: 'Tiefe', w: 7, align: 'r', get: s => de(s.tiefe) },
			{ header: 'Mächt', w: 8, align: 'r', get: s => de(s.maechtigkeit) },
			{ header: 'W', w: 4, get: s => s.wechselschichtung ? 'W' : '' },
			{ header: 'Substrat', w: 14, get: s => s.substrat || '' },
			{ header: 'Herk.', w: 9, get: s => s.substratherkunft || '' },
			{ header: 'Hum', w: 6, align: 'r', get: s => de(s.humusgehalt) },
			{ header: 'LagD', w: 6, align: 'r', get: s => de(s.lagerungsdichte) },
			{ header: 'Fest', w: 6, align: 'r', get: s => de(s.festigkeit) },
			{ header: 'Skel', w: 6, align: 'r', get: s => de(s.skelettgehalt) },
			{ header: 'Karb', w: 6, align: 'r', get: s => de(s.karbonatgehalt) },
			{ header: 'Gef.F', w: 8, get: s => s.gefuege?.form || '' },
			{ header: 'Gef.G', w: 8, get: s => s.gefuege?.groesse || '' },
			{ header: 'Gef.A', w: 8, get: s => s.gefuege?.auspraegung || '' },
			{ header: 'Fdw.D', w: 8, get: s => s.feindurchwurzelung?.dichte || '' },
			{ header: 'Fdw.H', w: 6, align: 'r', get: s => dePos(s.feindurchwurzelung?.haeufung) },
			{ header: 'Farbe', w: 11, get: s => s.farbe || '' },
			{ header: 'Lage', w: 9, get: s => s.schichtLage || '' },
			{ header: 'Pz', w: 6, get: s => s.perstruktionszone || '' },
			{ header: 'Besonderheiten', w: 18, get: s => s.besonderheiten || '' },
			{ header: 'Pr.Spanne', w: 9, get: s => s.probeentnahme?.spanne || '' },
			{ header: 'Pr.Fl', w: 7, align: 'r', get: s => de(s.probeentnahme?.flaeche) },
			{ header: 'Pr.Vol', w: 7, align: 'r', get: s => de(s.probeentnahme?.volumen) },
			{ header: 'nWSK', w: 7, align: 'r', get: (s, i) => this.schichtNWSK(bp, i) },
		];

		if (sub.length > 0) {
			// Dieselbe Klammer wie im Kurzprotokoll: Der Haken heisst „wechselt
			// mit der Zeile darunter" (wechsel-gruppen.ts). Hier stehen die
			// Substratzeilen fuer sich, es gibt keine Zeilen ohne Substrat.
			const lagen = wechselLagen(sub);
			const wSpalte = subCols.findIndex(c => c.header === 'W');
			y = this.renderColumnTable(doc, margin, y, totalW, 'Substratfolge (Grube-Profil)', subCols, sub,
				(idx, bandOben, hoehe, spaltenX, spaltenBreite) =>
					this.zeichneWechselKlammer(doc, lagen[idx], spaltenX[wSpalte], bandOben, hoehe, spaltenBreite[wSpalte]));
		} else {
			y = this.renderSectionHeader(doc, 'Substratfolge (Grube-Profil)', margin, y, totalW);
			doc.setFontSize(7);
			doc.setFont('helvetica', 'normal');
			doc.text('Keine Substratdaten erfasst.', margin, y);
			y += 5;
		}

		const hz = bp.Horizontfolge || [];
		if (hz.length > 0) {
			y = this.ensureSpace(doc, y + 2, 16);
			const hCols: PdfCol<HorizontZeile>[] = [
				{ header: 'Tiefe', w: 8, align: 'r', get: h => de(h.tiefe) },
				{ header: 'Mächt', w: 10, align: 'r', get: h => de(h.maechtigkeit) },
				{ header: 'Horizont 1', w: 27, get: h => h.horizonte?.[0] || '' },
				{ header: 'Horizont 2', w: 27, get: h => h.horizonte?.[1] || '' },
				{ header: 'Horizont 3', w: 28, get: h => h.horizonte?.[2] || '' },
			];
			y = this.renderColumnTable(doc, margin, y, totalW, 'Horizontfolge', hCols, hz);
		}
		return y;
	}

	/** Bemerkungen am Ende der Rueckseite (entfaellt, wenn leer). */
	private renderGrubeBemerkungen(doc: jsPDF, bp: AufnahmePunktDocument, margin: number, startY: number, totalW: number): number {
		const bem = bp.header?.bemerkungen;
		if (!bem) return startY;
		let y = this.ensureSpace(doc, startY + 2, 14);
		y = this.renderSectionHeader(doc, 'Bemerkungen', margin, y, totalW);
		doc.setFontSize(8);
		doc.setFont('helvetica', 'normal');
		const lines = doc.splitTextToSize(bem, totalW) as string[];
		doc.text(lines, margin, y);
		return y + lines.length * 4;
	}

	// ======================================================================
	// Profilbild (das Grubenbild aus dem gleichnamigen Reiter) als Anhang
	// ======================================================================

	/**
	 * Wer eine Profilbild-Seite bekommt: SEA-Punkte mit Protokoll, an denen
	 * etwas zu zeichnen ist - dieselbe Regel wie fuer den Reiter „Profilbild"
	 * (bohrpunkt-tabs.component.html). Kurzprotokolle und KA-Aufnahmen haben
	 * den Reiter nicht und bekommen auch keine Seite; ein Punkt ohne eine
	 * einzige Tiefe bekaeme eine leere.
	 */
	private bekommtProfilbild(bp: AufnahmePunktDocument): boolean {
		return verfahrenVon(bp) === 'sea95' && istPunktartMitProtokoll(bp.type) && hatGrubenbild(bp);
	}

	/**
	 * Eine Seite hinter dem Protokoll: das Grubenbild, am Seitenende die
	 * Fussnote. Scheitert das Rastern (keine Zeichenflaeche, Bild laedt
	 * nicht), steht DAS auf der Seite statt eines Bildes: Ein still fehlendes
	 * Bild faellt niemandem auf, und der Rest des PDFs soll nicht mit
	 * verlorengehen.
	 */
	private async haengeProfilbildAn(doc: jsPDF, bp: AufnahmePunktDocument): Promise<void> {
		let bild: RasterBild | null = null;
		let fehler: string | null = null;
		try {
			// Der Tag der Aufnahme fuer die Zeile am Wasserstand. Im Gastmodus ist
			// er schon vor dem Export entfernt (ohnePersonenangaben) und bleibt weg.
			bild = await grubenbildAlsBild(bp, this.formatDate(bp.header?.aufnahme?.datum) || null);
		} catch (e) {
			console.error('[PDF] Profilbild konnte nicht gezeichnet werden:', e);
			fehler = e instanceof Error ? e.message : String(e);
		}
		this.renderProfilbildSeite(doc, bp, bild, fehler);
	}

	private renderProfilbildSeite(doc: jsPDF, bp: AufnahmePunktDocument, bild: RasterBild | null, fehler: string | null): void {
		// Immer Hochformat, auch hinter der queren Rueckseite der Grube: Das Bild
		// ist etwa so hoch wie breit, quer bekaeme es mit dem Text darunter nur
		// zwei Drittel der Hoehe.
		doc.addPage('a4', 'portrait');
		const margin = this.marginLeft;
		const pageH = doc.internal.pageSize.getHeight();
		const contentWidth = doc.internal.pageSize.getWidth() - margin - this.marginRight;
		// Gruben tragen oben die laufende Kopfzeile - derselbe Abstand wie auf dem Formular.
		let y = this.marginTop + (isG(bp) ? 5 : 0);
		y = this.renderSectionHeader(doc, `Profilbild · Aufnahmepunkt ${this.punktTitel(bp)}`, margin, y, contentWidth, false);

		// Erst die Fussnote messen: Das Bild bekommt, was darueber uebrig bleibt.
		const zeilenHoehe = 2.8;
		doc.setFontSize(6.5);
		doc.setFont('helvetica', 'normal');
		const zeilen = doc.splitTextToSize(PROFILBILD_FUSSNOTE, contentWidth) as string[];
		const fussOben = pageH - this.marginBottom - 3 - zeilen.length * zeilenHoehe;

		if (bild) {
			const boxHoehe = fussOben - 4 - y;
			const f = Math.min(contentWidth / bild.breite, boxHoehe / bild.hoehe);
			const w = bild.breite * f;
			const h = bild.hoehe * f;
			doc.addImage(bild.dataUrl, bild.format, margin + (contentWidth - w) / 2, y, w, h, undefined, 'FAST');
		} else {
			doc.setFontSize(8);
			doc.text('Das Profilbild konnte nicht gezeichnet werden.' + (fehler ? ` (${fehler})` : ''), margin, y + 3);
		}

		// Die Fussnote am Seitenende, im Stil der Abkuerzungslegende (renderFooter):
		// feine Trennlinie, klein und blass - sie ist Beiwerk zum Bild (Nutzer-Wunsch 26.09.2026).
		doc.setDrawColor(180, 180, 180);
		doc.setLineWidth(0.15);
		doc.line(margin, fussOben, margin + contentWidth, fussOben);
		doc.setLineWidth(0.2);
		doc.setFontSize(6.5);
		doc.setFont('helvetica', 'normal');
		doc.setTextColor(140, 140, 140);
		zeilen.forEach((zeile, i) => doc.text(zeile, margin, fussOben + 3 + i * zeilenHoehe));
		doc.setTextColor(0, 0, 0);
	}
}
